// src/components/markdown/CodeBlock.tsx
import * as React from "react";
import CopyButton from "../chat/CopyButton";

interface CodeBlockProps {
  children?: React.ReactNode;
  className?: string;
  inline?: boolean;
}

// Shared chip treatment for code that should sit quietly in (or alongside)
// running text instead of getting a full block container.
const chipStyle: React.CSSProperties = {
  backgroundColor: "rgba(0, 95, 204, 0.06)",
  border: "1px solid rgba(0, 95, 204, 0.2)",
  padding: "1px 6px",
  borderRadius: "4px",
  fontFamily: "'Segoe UI', sans-serif",
  fontSize: "0.92em",
  color: "#1a1a1a",
  fontWeight: 500,
};

/**
 * Code rendering for chat content — designed for legal/contract text rather
 * than programming code.
 *
 * - Inline code renders as a small chip inside the sentence.
 * - A fenced block holding just a short single line with no language tag is
 *   almost always the model setting off a term or identifier, not real
 *   preformatted content — a full-width container would needlessly break the
 *   reading flow, so it renders as a standalone chip too.
 * - Everything else (multi-line content, or any block with a language tag)
 *   gets a "ledger" treatment: a firm top rule and a faint bottom rule with
 *   open sides, serif text to read as quoted document language, a
 *   hover-revealed copy button and, when a language tag is present, a small
 *   label punched into the top rule.
 */
const CodeBlock: React.FC<CodeBlockProps> = ({ children, className, inline }) => {
  const [isHovered, setIsHovered] = React.useState(false);

  // Extract text content from children for copy functionality
  const getTextContent = (node: React.ReactNode): string => {
    if (typeof node === "string") return node;
    if (typeof node === "number") return String(node);
    if (Array.isArray(node)) return node.map(getTextContent).join("");
    if (React.isValidElement(node) && node.props.children) {
      return getTextContent(node.props.children);
    }
    return "";
  };

  const textContent = getTextContent(children);

  // Extract language from className (e.g., "language-clause" or "language-suggestion")
  const language = className?.replace(/language-/, "") || "";

  // Common labels for legal content; an unmapped language tag is shown as-is.
  const labelMap: Record<string, string> = {
    suggestion: "Suggested Text",
    clause: "Clause",
    original: "Original",
    revised: "Revised",
    proposed: "Proposed",
    redline: "Redline",
    contract: "Contract",
    agreement: "Agreement",
  };
  const label = language ? labelMap[language.toLowerCase()] || language : "";

  // Inline code (backticks) — chip within the sentence
  if (inline) {
    return <code style={chipStyle}>{children}</code>;
  }

  // Never render an empty block — malformed model output can produce a
  // fence (or raw <pre>) with no content, and an empty frame is pure noise.
  const trimmedContent = textContent.trim();
  if (trimmedContent === "") {
    return null;
  }

  // Short unlabeled one-liner block — standalone chip instead of a container
  const isShortSingleLine = !trimmedContent.includes("\n") && trimmedContent.length <= 50;
  if (!language && isShortSingleLine) {
    return (
      <div style={{ margin: "8px 0" }}>
        <code style={chipStyle}>{trimmedContent}</code>
      </div>
    );
  }

  // Block code — ledger treatment: rules above and below, open sides
  return (
    <div
      style={{
        position: "relative",
        // Extra top margin makes room for the label that overlaps the rule
        margin: label ? "22px 0 14px" : "14px 0",
        borderTop: "2px solid rgba(28, 27, 24, 0.7)",
        borderBottom: "1px solid rgba(28, 27, 24, 0.22)",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {label && (
        <div
          style={{
            position: "absolute",
            top: "-8px",
            left: "14px",
            backgroundColor: "#ffffff",
            padding: "0 7px",
            lineHeight: "16px",
            fontSize: "10px",
            fontWeight: 700,
            color: "rgba(28, 27, 24, 0.55)",
            textTransform: "uppercase",
            letterSpacing: "1.4px",
            fontFamily: "'Segoe UI', sans-serif",
          }}
        >
          {label}
        </div>
      )}

      {/* Copy affordance, revealed on hover; CopyButton itself turns into a
          green tick on success */}
      <div
        style={{
          position: "absolute",
          top: "6px",
          right: "0",
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.2s ease",
        }}
      >
        <CopyButton textToCopy={textContent} className="code-block-copy-button" />
      </div>

      <pre
        style={{
          margin: 0,
          padding: "9px 36px 13px 0",
          backgroundColor: "transparent",
          overflowX: "auto",
          fontFamily: "Georgia, 'Iowan Old Style', 'Times New Roman', serif",
          fontSize: "14px",
          lineHeight: "1.65",
          color: "#24221e",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
        }}
      >
        <code
          style={{
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit",
            backgroundColor: "transparent",
          }}
        >
          {children}
        </code>
      </pre>
    </div>
  );
};

export default CodeBlock;
